import { useEffect, useRef } from 'react';

import { Search } from 'lucide-react';

export interface InputProps {
  id: string;
  variant?: 'text' | 'textarea' | 'search';
  error?: string;
  disabled?: boolean;
  name?: string;
  placeholder?: string;
  className?: string;
  value?: string;
  autoGrow?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export function Input({ id, variant = 'text', error, disabled, name, placeholder, className = '', value, autoGrow, onChange }: InputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const errorClass = error ? 'border-sd-error shadow-[0_0_0_1px] shadow-sd-error/20' : 'border-sd-border';
  const disabledClass = disabled ? 'opacity-50 cursor-not-allowed' : '';
  const autoGrowClass = autoGrow ? 'overflow-y-auto resize-none max-h-[200px]' : '';
  const transitionClass = variant === 'textarea' && autoGrow ? 'transition-colors duration-200' : 'transition-all duration-200';
  const baseClasses = `w-full border bg-sd-surface text-sd-text rounded-sd-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 ${transitionClass} placeholder:text-sd-text-muted placeholder:italic px-3 py-2 ${errorClass} ${disabledClass} ${autoGrowClass} ${className}`.trim();

  useEffect(() => {
    if (autoGrow && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [autoGrow, value]);

  if (variant === 'textarea') {
    return (
      <div>
        <textarea ref={textareaRef} id={id} disabled={disabled} name={name} placeholder={placeholder} className={baseClasses} value={value} onChange={onChange} />
        {error && <span className="text-sd-sm text-sd-error">{error}</span>}
      </div>
    );
  }
  if (variant === 'search') {
    const searchClasses = `${baseClasses} pl-10`;
    return (
      <div>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-sd-text-secondary">
            <Search size={16} />
          </div>
          <input id={id} disabled={disabled} name={name} placeholder={placeholder} className={searchClasses} value={value} onChange={onChange} />
        </div>
        {error && <span className="text-sd-sm text-sd-error">{error}</span>}
      </div>
    );
  }
  return (
    <div>
      <input id={id} disabled={disabled} name={name} placeholder={placeholder} className={baseClasses} value={value} onChange={onChange} />
      {error && <span className="text-sd-sm text-sd-error">{error}</span>}
    </div>
  );
}
